import { Product } from "@b2bportal/purchase-api";
import type { RbcInsuranceProduct } from "@b2bportal/rbc-insurance-api";
import { getObjectKeysAsObject } from "@checkout/helpers";
import { getPriceQuoteCipherText } from "@checkout/states/Cart/CartQuote/selectors";
import type { CartState } from "@checkout/states/Cart/types";
import { ParentState } from "@checkout/types";
import { assign, type DoneInvokeEvent } from "xstate";
import {
  InsuranceSelectedState,
  type ParentContextWithTripInsurance,
} from "./context";
import type {
  SelectProvinceEvent,
  SelectTripInsuranceOptionEvent,
} from "./events";
import type {
  RbcInsuranceProductType,
  TripInsuranceProductsResponse,
} from "./types";

type TripInsuranceSelectionContext = Pick<
  ParentContextWithTripInsurance,
  CartState.cartUpdate | CartState.cartQuote | ParentState.insurance
>;

/**
 * Applies an insurance selection to the context: syncs the cart update
 * add/remove product lists and records the selected option and product.
 */
export const applyTripInsuranceSelection = <
  C extends TripInsuranceSelectionContext,
>(
  context: C,
  option: InsuranceSelectedState,
  product?: RbcInsuranceProduct
): C => {
  const productsToAdd = context[ParentState.cartUpdate].addProducts;
  const productsWithoutInsurance =
    productsToAdd?.filter((p) => p.type !== Product.RbcInsurance) || [];

  const newAddProducts = product
    ? [
        ...productsWithoutInsurance,
        {
          type: Product.RbcInsurance,
          value: {
            opaqueShoppedProduct: product.opaqueShoppedProduct,
          },
        } satisfies RbcInsuranceProductType,
      ]
    : productsWithoutInsurance;

  const quotedProducts =
    context[ParentState.cartQuote].quoteBreakdown?.products;
  const insuranceProductsToRemove = (quotedProducts ?? [])
    .filter((p) => p.product.type === Product.RbcInsurance)
    .map((p) => ({
      product: Product.RbcInsurance,
      shoppedProductId: p.shoppedProductId,
    }));

  const newRemoveProducts =
    insuranceProductsToRemove.length > 0
      ? [
          ...context[ParentState.cartUpdate].removeProducts,
          ...insuranceProductsToRemove,
        ]
      : context[ParentState.cartUpdate].removeProducts;

  return {
    ...context,
    [ParentState.cartUpdate]: {
      ...context[ParentState.cartUpdate],
      addProducts: newAddProducts,
      removeProducts: newRemoveProducts,
    },
    [ParentState.insurance]: {
      ...context[ParentState.insurance],
      insuranceSelectedState: option,
      selectedProduct: product,
    },
  };
};

export const actions = {
  setTripInsuranceProducts: assign(
    (
      context: ParentContextWithTripInsurance,
      { data }: DoneInvokeEvent<TripInsuranceProductsResponse>
    ) => {
      const currentQuoteId = getPriceQuoteCipherText({ context });

      return {
        ...context,
        [ParentState.insurance]: {
          ...context[ParentState.insurance],
          products: {
            ...(context[ParentState.insurance]?.products ?? {}),
            [data.provinceCode]: data.products,
          },
          provinceCode: data.provinceCode,
          lastFetchedQuoteId: currentQuoteId,
        },
      };
    }
  ),
  setProvinceCode: assign(
    (context: ParentContextWithTripInsurance, event: SelectProvinceEvent) => ({
      ...context,
      [ParentState.insurance]: {
        ...context[ParentState.insurance],
        provinceCode: event.provinceCode,
      },
    })
  ),
  setTripInsuranceError: assign(
    (
      context: ParentContextWithTripInsurance,
      event: DoneInvokeEvent<Error>
    ) => ({
      ...context,
      [ParentState.insurance]: {
        ...context[ParentState.insurance],
        error: event.data,
        insuranceSelectedState: InsuranceSelectedState.UNAVAILABLE,
      },
    })
  ),
  skipTripInsurance: assign(
    (context: ParentContextWithTripInsurance, _event) => ({
      ...context,
      [ParentState.insurance]: {
        ...context[ParentState.insurance],
        insuranceSelectedState: InsuranceSelectedState.UNAVAILABLE,
      },
    })
  ),
  selectTripInsuranceOption: assign(
    (
      context: ParentContextWithTripInsurance,
      event: SelectTripInsuranceOptionEvent
    ) => applyTripInsuranceSelection(context, event.option, event.product)
  ),
  removeTripInsurance: assign((context: ParentContextWithTripInsurance) =>
    applyTripInsuranceSelection(context, InsuranceSelectedState.DECLINE)
  ),
  setTripInsuranceVisited: assign(
    (context: ParentContextWithTripInsurance, _event) => ({
      ...context,
      [ParentState.insurance]: {
        ...context[ParentState.insurance],
        visited: true,
      },
    })
  ),
  resetTripInsurance: assign(
    (context: ParentContextWithTripInsurance, _event) => {
      return {
        ...context,
        [ParentState.insurance]: {
          ...context[ParentState.insurance],
          selectedProduct: undefined,
          insuranceSelectedState: InsuranceSelectedState.NOT_SELECTED,
        },
      };
    }
  ),
};

export const ActionTypes = getObjectKeysAsObject(actions);
